import React, { useEffect, useRef } from 'react';
import { OrcamentoItem } from '../../../interfaces/OrcamentoCilia';
import { ProdutoNasajonItem } from '../../../api/orcamentoCiliaApi';
import HistoricoDaPeca from './HistoricoDaPeca';
import BuscaProdutoNasajon from './BuscaProdutoNasajon';
import '../../../shared/feedback/erro-de-tela.css';
import './ItemDetalheModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * Detalhe de UMA peça do orçamento (13/08/2026 — pedido do Victor).
 *
 * **Por que existe**: a tabela tinha 10 colunas e, mesmo com a régua de "célula é uma linha
 * só", descrição e selos ("obs.", "Pedida na v1 · não repete aqui") se atropelavam e o texto
 * cortava. A tabela ficou com 4 colunas de leitura rápida e **tudo o mais mora aqui** —
 * inclusive as AÇÕES (triagem, vínculo com o ERP, forçar compra), que ganharam espaço para
 * respirar em vez de disputar 150px de largura.
 *
 * Estilo do `RecepcaoActionsModal` (referência dada pelo Victor): overlay compartilhado
 * `add-car-modal-overlay`, cartão branco com cabeçalho, corpo em grade de informação.
 *
 * ⚠️ O item chega **derivado do dado fresco** pelo componente pai, não como snapshot do
 * clique: as ações daqui disparam mutations que reescrevem o orçamento, e um snapshot faria
 * o modal continuar exibindo o estado anterior à própria ação que o usuário acabou de fazer.
 */

interface Props {
  item: OrcamentoItem;
  /** Grupo em que a peça está — decide quais ações fazem sentido. */
  situacao: string;
  /** Conferência aberta: com ela fechada, tudo aqui é leitura. */
  isEditable: boolean;
  esteiras: Array<{ id: string; nome: string }>;
  /** Referência de preço (média de compras) e a régua do alerta. */
  referencia: number | null;
  margemPmcPct: number;
  mesesJanela: number;
  salvando: boolean;
  /** Vínculo com o produto do Nasajon em curso (o servidor revalida no ERP; demora segundos). */
  vinculandoProduto: boolean;
  /** Unidades do cadastro do ERP (at-11) — o item da requisição exige uma que exista lá. */
  unidades: Array<{ id: string; codigo: string; descricao: string | null }>;
  unidadesCarregando: boolean;
  /** A carga falhou: o select cai em leitura e a tela **diz** (regra da at-60). */
  unidadesErro: boolean;
  onFechar: () => void;
  onEsteiraChange: (itemId: string, esteiraId: string) => void;
  onUnidadeChange: (itemId: string, unidade: string) => void;
  onVipChange: (itemId: string, vip: boolean) => void;
  /** Vincula a peça a um produto escolhido na busca ao vivo do cadastro do Nasajon. */
  onVincularProduto: (itemId: string, produto: ProdutoNasajonItem) => void;
  onDesfazerVinculo: (itemId: string) => void;
  onVerHistoricoPreco: (item: OrcamentoItem) => void;
}

const formatCurrency = (v: string | number | null) => {
  const n = typeof v === 'number' ? v : parseFloat(v ?? '0');
  return Number.isFinite(n) ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
};

const STATUS_ERP_LABEL: Record<string, string> = {
  NAO_VERIFICADO: 'Não Verificado',
  EXISTENTE: 'Existente',
  A_CRIAR: 'A Criar',
  CRIADO: 'Criado',
};

const ItemDetalheModal: React.FC<Props> = ({
  item,
  situacao,
  isEditable,
  esteiras,
  referencia,
  margemPmcPct,
  mesesJanela,
  salvando,
  vinculandoProduto,
  unidades,
  unidadesCarregando,
  unidadesErro,
  onFechar,
  onEsteiraChange,
  onUnidadeChange,
  onVipChange,
  onVincularProduto,
  onDesfazerVinculo,
  onVerHistoricoPreco,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Esc fecha (mesmo comportamento dos modais da Oficina).
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFechar();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [onFechar]);

  const isHerdado = item.origemDiff === 'HERDADO';
  const foraDoFluxo = situacao === 'FORA_DO_FLUXO';
  // Herdado é imutável no complemento (o backend também recusa) e fora do fluxo não compra.
  const podeEditar = isEditable && !isHerdado && !foraDoFluxo;
  const alertaPreco = referencia !== null && parseFloat(item.preco) > referencia * (1 + margemPmcPct / 100);

  const qtdJaSolicitada = parseFloat(item.quantidadeJaSolicitada ?? '0');
  // A quantidade a comprar desconta só o que já foi solicitado em versão anterior. O
  // abatimento por saldo do ERP saiu em 20/08/2026 (ADR 0006).
  const qtdACompar = Math.max(parseFloat(item.quantidade) - qtdJaSolicitada, 0);

  return (
    <div className="add-car-modal-overlay" onClick={onFechar}>
      <div
        className="item-detalhe-modal-content"
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="item-detalhe-modal-header">
          <div className="header-info">
            <h2>{item.descricaoCilia}</h2>
            <span className="item-detalhe-codigo font-mono">{item.codigoCilia}</span>
          </div>
          <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="item-detalhe-modal-body">
          {/* Contexto do complemento: por que a peça está bloqueada, ou por que a quantidade
              não é a do orçamento. Era selo espremido na célula da descrição. */}
          {isHerdado && (
            <div className="item-detalhe-aviso">
              <strong>Pedida em versão anterior.</strong> Esta peça já foi solicitada
              {item.versaoOrigem ? ` na v${item.versaoOrigem}` : ''} deste orçamento e não é pedida de novo
              aqui — por isso não aceita triagem nem alteração de vínculo.
            </div>
          )}
          {item.origemDiff === 'QTD_AUMENTADA' && qtdJaSolicitada > 0 && (
            <div className="item-detalhe-aviso">
              <strong>Quantidade aumentada.</strong> O orçamento pede {item.quantidade}, sendo{' '}
              {qtdJaSolicitada} já solicitadas em versão anterior — esta versão cobre a diferença de{' '}
              {qtdACompar}.
            </div>
          )}
          {item.comentario && (
            <div className="item-detalhe-aviso item-detalhe-aviso--obs">
              <strong>Observação do orçamento:</strong> {item.comentario}
            </div>
          )}

          <h3>Dados da peça</h3>
          <div className="item-detalhe-grid">
            <div className="item-detalhe-campo">
              <span className="campo-label">Quantidade</span>
              <span className="campo-valor">
                {item.quantidade}
                {item.origemDiff === 'QTD_AUMENTADA' && qtdJaSolicitada > 0 && (
                  <small> (+{qtdACompar} a comprar)</small>
                )}
              </span>
            </div>
            <div className="item-detalhe-campo">
              <span className="campo-label">Valor unitário</span>
              <span className="campo-valor">{formatCurrency(item.preco)}</span>
            </div>
            <div className="item-detalhe-campo">
              <span className="campo-label">Referência de preço</span>
              <span className="campo-valor">
                {referencia !== null ? (
                  <button
                    type="button"
                    className={`pmc-link ${alertaPreco ? 'pmc-alert' : ''}`}
                    onClick={() => onVerHistoricoPreco(item)}
                    title={
                      alertaPreco
                        ? `Valor do orçamento mais de ${margemPmcPct}% acima da média de compras — clique para ver o histórico`
                        : `Média de ${item.mediaComprasQtd} compra(s) nos últimos ${mesesJanela} meses — clique para ver o histórico`
                    }
                  >
                    {formatCurrency(referencia)}
                  </button>
                ) : (
                  <span
                    className="value-read-only"
                    title={`Sem compras registradas no Nasajon nos últimos ${mesesJanela} meses`}
                  >
                    —
                  </span>
                )}
              </span>
            </div>
            {/* O campo "Estoque no Nasajon" saiu em 20/08/2026 (ADR 0006): o setor de compras
                não usa aquele saldo, e exibi-lo ao lado da compra sugeria que ele decide algo. */}
            <div className="item-detalhe-campo">
              <span className="campo-label">Tipo</span>
              <span className="campo-valor">{item.tipoItem === 'SERVICO' ? 'Serviço' : 'Peça'}</span>
            </div>
            <div className="item-detalhe-campo">
              <span className="campo-label">Cobertura</span>
              <span className="campo-valor">{item.tipoCobertura || '—'}</span>
            </div>
          </div>

          <h3>Triagem e cadastro no ERP</h3>
          <div className="item-detalhe-grid">
            <div className="item-detalhe-campo item-detalhe-campo--largo">
              <span className="campo-label">Esteira (triagem)</span>
              {foraDoFluxo ? (
                <span
                  className="value-read-only"
                  title={
                    item.tipoItem === 'SERVICO'
                      ? 'Item de serviço (mão de obra) — não há peça a comprar.'
                      : 'O orçamentista do Cilia definiu reparo em vez de troca: a peça será recuperada pela oficina, então não há compra.'
                  }
                >
                  {item.tipoItem === 'SERVICO' ? 'Serviço (mão de obra)' : 'Reparo pela oficina (sem troca)'}
                </span>
              ) : !podeEditar ? (
                <span className="value-read-only">{item.esteiraNome || '—'}</span>
              ) : (
                <select
                  className={`cilia-select ${!item.esteiraIdSoac ? 'pending-input' : ''}`}
                  value={item.esteiraIdSoac || ''}
                  onChange={(e) => onEsteiraChange(item.id, e.target.value)}
                >
                  <option value="">Selecione...</option>
                  {esteiras.map((est) => (
                    <option key={est.id} value={est.id}>
                      {est.nome}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="item-detalhe-campo item-detalhe-campo--largo">
              <span className="campo-label">Produto no Nasajon</span>
              {foraDoFluxo ? (
                <span className="value-read-only">—</span>
              ) : !podeEditar ? (
                <span className="value-read-only font-mono">{item.codigoNasajon || '—'}</span>
              ) : (
                <BuscaProdutoNasajon
                  codigoAtual={item.codigoNasajon}
                  vinculado={item.produtoStatus === 'EXISTENTE' && !!item.produtoNasajonId}
                  disabled={salvando}
                  vinculando={vinculandoProduto}
                  onVincular={(produto) => onVincularProduto(item.id, produto)}
                  onDesvincular={() => onDesfazerVinculo(item.id)}
                />
              )}
            </div>

            <div className="item-detalhe-campo item-detalhe-campo--largo">
              <span className="campo-label">Status no ERP</span>
              <span className="campo-valor erp-cell">
                <span
                  className={`erp-status-tag status-${item.produtoStatus.toLowerCase()}`}
                  title={
                    item.produtoStatus === 'EXISTENTE'
                      ? 'Peça reconhecida: já existe no cadastro do Nasajon — não será criada duplicata'
                      : item.produtoStatus === 'A_CRIAR'
                        ? 'Peça não reconhecida no cadastro do Nasajon — será criada no envio'
                        : undefined
                  }
                >
                  {STATUS_ERP_LABEL[item.produtoStatus] ?? item.produtoStatus}
                </span>
                {/* O "desfazer vínculo" mora no campo Produto no Nasajon, ao lado do código. */}
              </span>
            </div>

            {/*
              UNIDADE DE MEDIDA (at-11) — passou a ser escolhida aqui porque vai no item da
              requisição, e o palpite do sistema (`UN`) erra em peça vendida em kit, metro ou jogo.

              🔴 **Só em peça que vai ser CRIADA.** Produto que já existe no cadastro usa a unidade
              dele (medido: 12/12 itens reais), e o backend recusa a troca — a requisição não pode
              contradizer o produto que ela referencia. Mostrar o select aqui prometeria uma
              edição que o servidor nega.
            */}
            <div className="item-detalhe-campo item-detalhe-campo--largo">
              <span className="campo-label">Unidade de medida</span>
              {foraDoFluxo ? (
                <span className="value-read-only">—</span>
              ) : item.produtoStatus === 'EXISTENTE' ? (
                <span
                  className="value-read-only"
                  title="A peça já existe no Nasajon: a requisição usa a unidade do cadastro dele, e por isso ela não é editável aqui."
                >
                  {item.unidade || '—'} <small>(do cadastro do ERP)</small>
                </span>
              ) : !podeEditar ? (
                <span className="value-read-only">{item.unidade || '—'}</span>
              ) : (
                <select
                  className="cilia-select"
                  value={item.unidade || ''}
                  onChange={(e) => onUnidadeChange(item.id, e.target.value)}
                  disabled={salvando || unidadesCarregando}
                  title="Unidade com que a peça é comprada. Vai no item da requisição."
                >
                  {/*
                    ⚠️ A unidade atual entra na lista mesmo que não esteja no cadastro do ERP: sem
                    isso, um valor legado sumiria do select e a peça pareceria estar sem unidade —
                    e salvar sem tocar no campo o apagaria.
                  */}
                  {item.unidade && !unidades.some((u) => u.codigo === item.unidade) && (
                    <option value={item.unidade}>{item.unidade} (fora do cadastro do ERP)</option>
                  )}
                  {unidades.map((u) => (
                    <option key={u.id} value={u.codigo}>
                      {u.codigo}
                      {u.descricao ? ` — ${u.descricao}` : ''}
                    </option>
                  ))}
                </select>
              )}
              {unidadesErro && (
                // Regra da at-60: lista vazia por erro seria lida como "o ERP não tem unidades".
                <span className="erro-de-tela">
                  Não foi possível carregar as unidades do Nasajon. A unidade atual foi mantida; recarregue a
                  página para tentar de novo.
                </span>
              )}
            </div>

            {/*
              CAMINHO VIP (at-11, decisão do Victor + Alessandra, 18/09/2026).

              🔴 A peça já foi comprada na rua e o comprador **não** deve comprá-la. Marcar aqui faz
              a peça ir para uma requisição SEPARADA, cujo "Motivo da Requisição" começa com o
              marcador que avisa isso — é convenção de texto livre no ERP, sem campo próprio e sem
              validação, e é por isso que quem a escreve é o sistema.
            */}
            {!foraDoFluxo && (
              <div className="item-detalhe-campo item-detalhe-campo--largo">
                <span className="campo-label">Já comprada na rua (CAMINHO VIP)</span>
                {!podeEditar ? (
                  <span className="value-read-only">{item.vip ? 'Sim' : 'Não'}</span>
                ) : (
                  <label className="item-detalhe-checkbox">
                    <input
                      type="checkbox"
                      checked={!!item.vip}
                      disabled={salvando}
                      onChange={(e) => onVipChange(item.id, e.target.checked)}
                    />
                    <span>
                      Esta peça já foi comprada — vai numa requisição separada, avisando o comprador para{' '}
                      <strong>não</strong> comprar.
                    </span>
                  </label>
                )}
              </div>
            )}
          </div>

          {/* O botão "Forçar compra" existia aqui até 20/08/2026 (ADR 0006): era o escape da
              regra de estoque. Sem a regra, toda peça com troca autorizada é comprada, então
              não há decisão do sistema de que discordar. */}

          {/*
            Histórico da peça (at-05): fecha a promessa que a hint acima já fazia — "fica
            registrado na auditoria" só era verdade no banco. Fica por último, depois das
            ações, porque é leitura de apoio; a conferência continua sendo o assunto do modal.
          */}
          <div className="item-detalhe-historico">
            <h4>Histórico desta peça</h4>
            <HistoricoDaPeca itemId={item.id} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemDetalheModal;
